import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  resolveProcessingErrorMessage,
  resolveProcessingPolicyAdvisories,
  resolveProcessingPolicyViolationMessage,
  resolveProcessingPolicyViolations,
  resolveProcessingRejectionSummaryMessage,
  hasProcessingAdvisories,
} from '@/helpers/media-processing-policy-messages.helper';

type Translate = (key: TranslationKey, params?: Record<string, string | number>) => string;

export function processingInsightStatusLabelKey(insight: ProcessingInsightDto): TranslationKey {
  if (insight.readiness === 'rejected') {
    return 'media.processing.status.rejected';
  }
  if (insight.readiness === 'failed') {
    return 'media.processing.status.failed';
  }
  if ((insight.readiness === 'ok' || insight.playoutReady) && hasProcessingAdvisories(insight)) {
    return 'media.processing.status.readyWithLimitations';
  }
  if (insight.readiness === 'ok' || insight.playoutReady) {
    return 'media.processing.status.ready';
  }
  if (insight.status === 'not_started' || insight.readiness === 'not_started') {
    return 'media.processing.status.notStarted';
  }
  if (insight.status === 'queued' || insight.status === 'pending') {
    return 'media.processing.status.queued';
  }
  if (insight.status === 'inspecting') {
    return 'media.processing.status.inspecting';
  }
  if (insight.status === 'planned') {
    return 'media.processing.status.planned';
  }
  if (insight.status === 'running') {
    return 'media.processing.status.rendering';
  }
  return 'media.processing.status.processing';
}

export function buildProcessingInsightTooltip(
  insight: ProcessingInsightDto,
  t: Translate,
  options?: { includeInstructionsHint?: boolean },
): string {
  const lines: string[] = [t(processingInsightStatusLabelKey(insight))];

  if (insight.rejection && resolveProcessingPolicyViolations(insight).length > 0) {
    const violations = resolveProcessingPolicyViolations(insight);
    const summary = resolveProcessingRejectionSummaryMessage(violations, t);
    lines.push(summary);
    const listAll = violations.length > 1;
    for (const violation of violations) {
      const violationText = resolveProcessingPolicyViolationMessage(violation, t);
      if (!listAll && violationText === summary) {
        continue;
      }
      lines.push(`• ${violationText}`);
    }
  } else if (hasProcessingAdvisories(insight)) {
    for (const advisory of resolveProcessingPolicyAdvisories(insight)) {
      lines.push(`• ${resolveProcessingPolicyViolationMessage(advisory, t)}`);
    }
  } else if (insight.error?.message) {
    lines.push(resolveProcessingErrorMessage(insight.error, t));
  } else if (insight.highlights.length > 0) {
    lines.push(...insight.highlights.slice(0, 6));
  }

  if (options?.includeInstructionsHint) {
    lines.push(t('media.processing.instructionsModal.openHint'));
  }

  return lines.join('\n');
}
